import { DATE_RANGES } from 'Pages/Reports/Enum';
import pdfMake from 'pdfmake/build/pdfmake';
import { vfs } from 'pdfmake/build/vfs_fonts';
import { areRangesEqual, formatDate } from 'Util/dateUtil';

pdfMake.vfs = vfs;

const report_orange = '#FFB81D';
const report_blue = '#9DD8D7';
const report_green = '#c4e5b8';
const report_bright_blue = '#6CC24A';
const report_gray = '#abb3bf';
const report_light_gray = '#edeff2';
const report_total_gray = '#dce0e5';
const report_core_total = '#708E99';
const report_grand_total = '#4F868E';

const getHeaderDataFromElement = (element) => {
  let elementText = element.textContent;

  elementText = elementText.includes('Date')
    ? elementText.slice(5)
    : elementText;
  elementText = elementText === 'Account' ? 'Acct.' : elementText;

  let styleName = element.className.includes('money_header')
    ? ['tableHeader', 'moneyHeader']
    : 'tableHeader';

  return {
    text: elementText,
    noWrap: true,
    style: styleName,
    maxHeight: 20,
  };
};
const getCellDataFromElement = (element, colIndex, isDetailView) => {
  let elementText = element.text || element.textContent || '';
  let isRevenue = false;
  let isAggCell = element.className.includes('agg_cell');

  let styleNames = [];
  if (
    element.className.includes('grand_total') &&
    element.className.includes('money_cell')
  ) {
    styleNames.push('grandTotal');
    styleNames.push('grandTotalMoney');
  } else if (element.className.includes('grand_total')) {
    styleNames.push('grandTotal');
  } else if (element.className.includes('core_header')) {
    styleNames.push('coreTableHeader');
  } else if (isAggCell) {
    styleNames.push('aggCell');
  } else if (
    element.className.includes('activity_header') ||
    element.className.includes('abr_header')
  ) {
    styleNames.push('tableSubHeader');
  } else if (element.className.includes('money_cell')) {
    styleNames.push('moneyCell');
  } else {
    styleNames.push('tableData');
  }
  if (element.className.includes('abr_code')) {
    styleNames.push('abrCode');
  }

  let isCoreTotal =
    element.className.includes('core_total') ||
    element.className.includes('core_category_total');
  let isDescriptionField = element.id.includes('_description');
  let isBudgetField = element.id.includes('_budget');

  let isTotalRowVendor = isCoreTotal && element.id.includes('_vendor');
  let isTotalRowDescription = isCoreTotal && isDescriptionField;

  let colSpan = element.colSpan
    ? element.colSpan
    : isTotalRowVendor
    ? 0
    : isTotalRowDescription
    ? 2
    : isAggCell
    ? 1
    : isDescriptionField
    ? 2
    : isBudgetField
    ? 0
    : 1;

  return {
    skip: isTotalRowVendor,
    addBlankAfter: isTotalRowDescription,
    text: elementText,
    noWrap: colIndex !== 2 && (!isDetailView || colIndex !== 3),
    style: styleNames,
    // ...(isCoreTotal && { margin: [4, 6] }),
    ...(elementText.startsWith('-') && { margin: [4, 4, 12, 4] }),
    colSpan: colSpan,
    maxHeight: 20,
    border: [
      true,
      element.className.includes('agg_cell'),
      true,
      element.id.includes('grouping_end'),
    ],
    ...(element.className.includes('core_header') &&
      isRevenue && { fillColor: report_bright_blue }),
    ...(element.className.includes('activity_header') && {
      fillColor: report_blue,
    }),
    ...(element.className.includes('abr_header') && {
      fillColor: report_gray,
    }),
    ...(element.className.includes('core_category_total') && {
      fillColor: report_green,
    }),
    ...(element.className.includes('core_total') && {
      fillColor: report_core_total,
      color: 'white',
    }),
    ...(element.className.includes('grand_total') && {
      fillColor: report_grand_total,
      color: 'white',
    }),
    ...(element.className.includes('detail_cell_gray') && {
      fillColor: report_light_gray,
    }),
  };
};

const getTableLayout = (hasGrandTotal) => {
  return {
    hLineWidth: () => {
      return 1;
    },
    vLineWidth: (i, node) => {
      return i === 0 || i === node.table.widths.length ? 1 : 0;
    },
    hLineColor: !hasGrandTotal
      ? (i, node) => {
          return i === 0 || i === 1 || i === node.table.body.length
            ? report_core_total
            : report_core_total;
        }
      : (i, node) => {
          return i === 0 || i === 1 || i === node.table.body.length
            ? report_core_total
            : i === node.table.body.length - 1
            ? 'black'
            : report_core_total;
        },
    vLineColor: (i, node) => {
      return i === 0 || i === node.table.widths.length
        ? report_core_total
        : null;
    },
  };
};

const getGroupedTableData = ({ tableRows, headerRows, widths, footerRows }) => {
  let isDetailView = headerRows.length === 10;
  let groupingIndex = 0;
  let tableDataAsRows = tableRows.reduce((rowGroups, rowData) => {
    let elementGroupId = [...rowData][0].id;

    if (!rowGroups[groupingIndex]) {
      rowGroups[groupingIndex] = [];
    }
    let rows = [...rowData]
      .map((element, index) =>
        getCellDataFromElement(element, index, isDetailView),
      )
      .filter((cell) => !cell.skip);
    let bigCell = rows.find((cell) => cell.addBlankAfter);
    let addIndex = rows.indexOf(bigCell);
    addIndex >= 0 &&
      rows.splice(addIndex + 1, 0, { text: ' ', style: bigCell.style });
    rowGroups[groupingIndex].push(rows);

    if (elementGroupId.includes('grouping_end')) {
      groupingIndex = groupingIndex + 1;
    }
    return rowGroups;
  }, []);

  let tableContent = tableDataAsRows.map((rowData) => [
    headerRows.map((a) => {
      return { ...a };
    }),
    ...rowData,
  ]);

  return tableContent.map((content, index) => ({
    style: 'tableStyle',
    ...(index > 0 && { pageBreak: 'before' }),
    table: {
      width: '100%',
      alignment: 'center',
      widths: widths,
      headerRows: 1,
      dontBreakRows: true,
      body: [
        ...content,
        ...(index === tableContent.length - 1 && footerRows?.length
          ? [footerRows]
          : []),
      ],
    },
    layout: getTableLayout(
      index === tableContent.length - 1 && footerRows?.length,
    ),
  }));
};

const getReportDocDefinition = (title, rows) => {
  let reportTitle = title || 'BOS Report';
  let reportIndex = reportTitle.indexOf('Report');
  let mainTitle = reportTitle.slice(0, reportIndex + 6);
  let subTitle = `${reportTitle.slice(reportIndex + 6)}`;

  let headerRows = [...document.querySelectorAll('thead tr th')].map(
    (thElement) => getHeaderDataFromElement(thElement),
  );

  let footerRows = [...document.querySelectorAll('tfoot tr td')]
    .map((tdElement, index) =>
      getCellDataFromElement(tdElement, index, headerRows.length === 10),
    )
    .filter((cell) => cell.colSpan > 0);

  let moneyWidths = Array(6).fill(74);
  let widths =
    headerRows.length === 10
      ? [52, 42, 70, '*', ...moneyWidths]
      : [52, 42, '*', ...moneyWidths];

  let trRows = [...document.querySelectorAll('tbody tr')].map((trElement) =>
    trElement.querySelectorAll('td'),
  );
  let tableRows = rows || trRows;

  let tableContent = getGroupedTableData({
    tableRows,
    headerRows,
    widths,
    footerRows,
  });

  return {
    info: {
      title: reportTitle,
      subject: 'Transaction report',
      creator: 'BOS using pdfmake',
    },
    pageOrientation: 'landscape',
    pageMargins: [18, 10, 18, 14],
    footer: function (currentPage, pageCount) {
      return {
        columns: [
          {
            text: `${formatDate(new Date())}`,
            style: 'footer',
          },
          ...(currentPage === 1
            ? []
            : [
                {
                  text: `Page ${currentPage} of ${pageCount}`,
                  style: ['footer', 'right'],
                },
              ]),
        ],
      };
    },
    content: [
      {
        text: [
          {
            text: mainTitle,
            style: 'header',
          },
          {
            text: subTitle,
            style: 'header',
            fontSize: 10,
          },
        ],
      },
      [...tableContent],
    ],
    styles: {
      footer: {
        fontSize: 9,
        alignment: 'left',
        margin: [12, 0, 12, 0],
      },
      right: {
        alignment: 'right',
      },
      header: { fontSize: 11, alignment: 'center', bold: true },
      tableStyle: {
        margin: [0, 8, 0, 0],
      },
      tableHeader: {
        margin: [4, 2],
        fontSize: 11,
        // bold: true,
      },
      moneyHeader: {
        alignment: 'right',
      },
      tableSubHeader: {
        margin: [4, 2],
        fontSize: 13,
        bold: true,
      },
      coreTableHeader: {
        margin: [4, 2],
        fontSize: 15,
        bold: true,
        fillColor: report_orange,
      },
      moneyCell: {
        margin: 4,
        fontSize: 9,
        alignment: 'right',
      },
      abrCode: {
        alignment: 'right',
      },
      aggCell: {
        margin: [4, 6],
        fontSize: 11,
        bold: true,
        alignment: 'right',
        fillColor: report_total_gray,
        lineHeight: 1,
      },
      grandTotal: {
        margin: [4, 10],
        fontSize: 12,
        bold: true,
        alignment: 'right',
      },
      grandTotalMoney: {
        fontSize: 11,
      },
      tableData: {
        fontSize: 9,
        margin: 4,
      },
    },
  };
};

export const generatePDFReport = (
  title,
  rows,
  download = false,
  callback = () => {},
  onError = () => {},
) => {
  if (!rows) {
    onError({ message: 'No data provided' });
    return;
  }

  try {
    let docDefinition = getReportDocDefinition(title, rows);
    download &&
      pdfMake
        .createPdf(docDefinition)
        .download(title?.replaceAll(' ', '_') + '.pdf' || 'BOS_Report.pdf');

    !download && pdfMake.createPdf(docDefinition).open();

    callback();
  } catch (e) {
    onError(e);
  }
};

export const getTitleForPDF = (reportOptions, showDetails) => {
  let matchingRangeObject = DATE_RANGES.find((dro) =>
    areRangesEqual(dro.dateRange, reportOptions.dateRange),
  );
  let rangeString =
    matchingRangeObject?.label ||
    `${formatDate(reportOptions.dateRange[0])}-${formatDate(
      reportOptions.dateRange[1],
    )}`;

  return `${
    !reportOptions.organizations?.length
      ? ''
      : reportOptions.organizations?.length <= 3
      ? reportOptions.organizations.map((org) => org.name).join(', ') + ' '
      : ''
  }${showDetails ? 'Detail' : 'Summary'} Report (${
    reportOptions.dateField === 'date_cleared' ? 'Cleared' : 'Entered'
  } ${rangeString})`;
};
